import Box from '@mui/material/Box';
import CreateIcon from '@mui/icons-material/Create';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router';
import { mindtimeWazuhFileMonitoringApi } from 'src/api/mindtime/wazuhFileMonitoringApi';
import { Table } from 'src/common';
import { getSecurityFimEventDetailHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type {
  WazuhFimAgentSummaryData,
  WazuhFimEventItem,
} from 'src/types/mindtime/wazuhFileMonitoring';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  buildFimAgentEventsQuery,
  buildFimUserFilterOptions,
  FIM_CHANGE_TYPE_FILTER_OPTIONS,
  FIM_RULE_LEVEL_FILTER_OPTIONS,
  FIM_SEVERITY_FILTER_OPTIONS,
  FimSummaryKpiGrid,
  getFimEventId,
  getFimEventListColumns,
  type FimAgentEventsFilters,
} from '../../file-integrity-monitoring/fileMonitoringDetailModel';
import { getAgentFileMonitoringPreview } from '../../preview/fileIntegrity';
import {
  EdrDataCard,
  EdrDataToolbar,
  type EdrToolbarFilter,
} from '../../shared/edrPrimitives';

const PAGE_SIZE = 25;

type EndpointFileMonitoringTabProps = {
  systemWazuhAgentId: string;
};

const EndpointFileMonitoringTab: FC<EndpointFileMonitoringTabProps> = ({ systemWazuhAgentId }) => {
  const navigate = useNavigate();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'file-integrity-monitoring');
  const canSummary = canWidget('summary');
  const canList = canWidget('events');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FimAgentEventsFilters>({});
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState<WazuhFimAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhFimEventItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const openFimEventDetail = useCallback(
    (item: WazuhFimEventItem) => {
      const id = getFimEventId(item);
      if (!id) return;
      navigate(getSecurityFimEventDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      setSummary(canSummary ? getAgentFileMonitoringPreview(agentId).summary : null);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhFileMonitoringApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointFileMonitoringTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const preview = getAgentFileMonitoringPreview(agentId);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhFileMonitoringApi
      .getEvents(buildFimAgentEventsQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointFileMonitoringTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    page,
    rowsPerPage,
    search,
    filters.changeType,
    filters.severityBand,
    filters.ruleLevel,
    filters.user,
    wait,
    isPreviewMode,
    canList,
  ]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getFimEventId(item) || item.filePath,
      })),
    [items]
  );

  const setFilter = (key: keyof FimAgentEventsFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({});
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const userOptions = useMemo(
    () => buildFimUserFilterOptions(items, filters.user ?? ''),
    [items, filters.user]
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'change',
        label: 'Change Type',
        icon: <CreateIcon style={{ fontSize: 14 }} />,
        options: FIM_CHANGE_TYPE_FILTER_OPTIONS,
        value: filters.changeType ?? '',
        multiple: true,
        onChange: (value) => setFilter('changeType', value),
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: FIM_SEVERITY_FILTER_OPTIONS,
        value: filters.severityBand ?? '',
        multiple: true,
        onChange: (value) => setFilter('severityBand', value),
      },
      {
        id: 'ruleLevel',
        label: 'Rule Level',
        options: FIM_RULE_LEVEL_FILTER_OPTIONS,
        value: filters.ruleLevel ?? '',
        multiple: true,
        onChange: (value) => setFilter('ruleLevel', value),
      },
      {
        id: 'user',
        label: 'User',
        icon: <PersonIcon style={{ fontSize: 14 }} />,
        options: userOptions,
        value: filters.user ?? '',
        multiple: true,
        onChange: (value) => setFilter('user', value),
      },
    ],
    [filters.changeType, filters.severityBand, filters.ruleLevel, filters.user, userOptions]
  );

  const columns: Column[] = useMemo(() => getFimEventListColumns('endpoint'), []);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canSummary ? <FimSummaryKpiGrid summary={summary} /> : null}

      {canList ? (
      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search files…"
            filters={toolbarFilters}
            onReset={handleReset}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onRowClick={(row) => openFimEventDetail(row as unknown as WazuhFimEventItem)}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointFileMonitoringTab;
